/**
 * Health Hooks
 *
 * The deployed versions of the API and the brain — read once, for an About
 * screen — and whether the AI service (the brain) is answering, for the screens
 * that use it to say so calmly when it is not (Journey PRD § Brain API
 * Failures: "Some features temporarily unavailable. Retrying…").
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isAiUnavailable } from '../client/api-error';
import { useApiClient } from '../providers/SdkProvider';
import {
  HealthService,
  type BrainHealthReport,
  type PlatformApiStatus,
} from '../services/health.service';

export const healthKeys = {
  all: ['health'] as const,
  apiStatus: () => [...healthKeys.all, 'apiStatus'] as const,
  brain: () => [...healthKeys.all, 'brain'] as const,
};

/** How often a brain found down is asked again ("Retrying…"). */
export const BRAIN_RECHECK_MS = 30_000;

/**
 * - `checking` — asked, no answer since the moment in question yet;
 * - `down`     — `GET /api/health/brain` refused (503: the brain is
 *                unreachable or unhealthy — a bad model id, a rejected key —
 *                or the request itself failed): the AI cannot answer;
 * - `up`       — the brain answered.
 */
export type BrainStatus = 'checking' | 'down' | 'up';

/**
 * Is the AI service answering — asked from a moment on (`since`: when an AI
 * call just failed as unavailable, e.g. a CoPilot turn's `provider_unavailable`)?
 * Idle while `since` is null, so no screen pays a health call for nothing.
 * Only an answer given AFTER `since` counts: an "up" cached from before the
 * failure says nothing about now. While down it asks again every
 * `BRAIN_RECHECK_MS` until the brain answers; `recheck` asks at once.
 */
export function useBrainStatusSince(since: number | null): {
  status: BrainStatus;
  recheck: () => void;
  rechecking: boolean;
} {
  const client = useApiClient();
  const service = new HealthService(client);
  const query = useQuery<BrainHealthReport, Error>({
    queryKey: healthKeys.brain(),
    queryFn: () => service.getBrainHealth(),
    enabled: since !== null,
    staleTime: 0,
    retry: false,
    refetchInterval: (q) =>
      since !== null && !(q.state.status === 'success' && q.state.dataUpdatedAt >= since)
        ? BRAIN_RECHECK_MS
        : false,
  });

  const status: BrainStatus =
    since === null
      ? 'checking'
      : query.isSuccess && query.dataUpdatedAt >= since
        ? 'up'
        : query.isError && query.errorUpdatedAt >= since
          ? 'down'
          : 'checking';
  const { refetch } = query;

  return {
    status,
    recheck: useCallback(() => void refetch(), [refetch]),
    rechecking: query.isFetching,
  };
}

/**
 * A READ that failed because the AI service did not answer (`AI_UNAVAILABLE`)
 * reads again by itself once the AI answers — the "Retrying…" of the calm
 * notice (Journey PRD § Brain API Failures). Returns whether `error` is that
 * outage, for the surface to show the notice in place of its own failure copy.
 *
 * From the failure on, the brain's health is asked (`useBrainStatusSince`)
 * until it answers; `retry` runs when the brain is seen DOWN and then UP again.
 * A brain that answers "up" at once (it is reachable, but the AI behind it is
 * not) does not loop the read — the notice's own Retry is the way then.
 *
 * Reads only: a write is retried by the person, never on their behalf.
 */
export function useAiOutageRecovery(error: unknown, retry: () => void): boolean {
  const unavailable = isAiUnavailable(error);
  // Each failed read asks afresh from its own moment; any other outcome stops asking.
  const [since, setSince] = useState<number | null>(null);
  useEffect(() => {
    setSince(unavailable ? Date.now() : null);
  }, [unavailable, error]);
  const { status } = useBrainStatusSince(since);

  const retryRef = useRef(retry);
  useEffect(() => {
    retryRef.current = retry;
  }, [retry]);
  // The moment the brain was last seen down — a recovery counts only for it.
  const downSinceRef = useRef<number | null>(null);
  useEffect(() => {
    if (since === null) return;
    if (status === 'down') downSinceRef.current = since;
    else if (status === 'up' && downSinceRef.current === since) {
      downSinceRef.current = null;
      retryRef.current();
    }
  }, [since, status]);

  return unavailable;
}

/** The API's and the brain's reported versions; each answers on its own. */
export function useDeployedVersions() {
  const client = useApiClient();
  const service = new HealthService(client);
  const api = useQuery<PlatformApiStatus, Error>({
    queryKey: healthKeys.apiStatus(),
    queryFn: () => service.getApiStatus(),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
  const brain = useQuery<BrainHealthReport, Error>({
    queryKey: healthKeys.brain(),
    queryFn: () => service.getBrainHealth(),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
  return { api, brain };
}
